/**
 * The pieces every analytics screen is built from.
 *
 * They live here rather than in the routes because the role dashboards and the deep pages
 * show the same panels to different people: a finance manager's home screen and the sales
 * page both plot revenue, and two implementations of that would eventually disagree about
 * what "revenue" means on a chart.
 *
 * **Every panel handles its own empty state, and an empty panel says which question came
 * back with nothing.** "No data" on a dashboard of six charts tells the reader nothing
 * about which of the six is broken. A window with no trade and a rollup that was never
 * built look identical from here, so the message names the window instead of guessing.
 *
 * **Charts are sized in the page, not by their content.** `ResponsiveContainer` measures
 * its parent, so every chart sits in a fixed-height box and the skeleton shown while the
 * query is in flight is the same height. Nothing on the page jumps when the data lands.
 */

import type { ReactNode } from 'react'
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { QueryState } from '@/components/QueryState'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  CHANNEL_LABELS,
  formatCompact,
  formatDay,
  formatDeltaPct,
  formatFullDay,
  formatMoney,
  formatPct,
  toneOf,
  WINDOWS,
  type CategorySlice,
  type ChannelSlice,
  type CoverRow,
  type ProductRow,
  type RepAttainment,
  type SegmentSlice,
  type SeriesPoint,
} from '@/features/analytics/api'
import { cn } from '@/lib/cn'

/**
 * One palette for the whole module, in hex rather than Tailwind classes.
 *
 * Recharts paints into SVG attributes, so a class name never reaches the fill. These are
 * the brand and status colours from the theme, written out once here so a chart cannot
 * drift into a blue the rest of the app does not use.
 */
export const CHART_COLORS = {
  net: '#2563eb',
  margin: '#16a34a',
  cost: '#f59e0b',
  danger: '#dc2626',
  muted: '#94a3b8',
} as const

/** For categorical series -- categories, segments, channels. Repeats past six. */
const SERIES_COLORS = ['#2563eb', '#16a34a', '#f59e0b', '#8b5cf6', '#ec4899', '#0891b2']

const AXIS = { fontSize: 11, fill: '#94a3b8' } as const

const TOOLTIP_STYLE = {
  contentStyle: {
    borderRadius: '0.5rem',
    border: '1px solid #cbd5e1',
    fontSize: '0.75rem',
    padding: '0.5rem 0.75rem',
  },
} as const

// --- frames ----------------------------------------------------------------------------

/**
 * A card with a chart in it, in whichever of the four states it is in.
 *
 * The skeleton is the height of the chart on purpose -- see the module note.
 */
export function ChartCard({
  title,
  description,
  isLoading,
  error,
  isEmpty,
  emptyMessage,
  height = 240,
  action,
  children,
}: {
  title: string
  description?: string
  isLoading: boolean
  error: unknown
  isEmpty: boolean
  emptyMessage: string
  height?: number
  action?: ReactNode
  children: ReactNode
}) {
  return (
    <Card>
      <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4 dark:border-slate-800">
        <div>
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">{title}</h2>
          {description && (
            <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">{description}</p>
          )}
        </div>
        {action}
      </div>
      <CardBody>
        <QueryState
          isLoading={isLoading}
          error={error}
          isEmpty={isEmpty}
          emptyMessage={emptyMessage}
          skeleton={<Skeleton style={{ height }} className="w-full" />}
        >
          <div style={{ height }}>{children}</div>
        </QueryState>
      </CardBody>
    </Card>
  )
}

/** A headline figure. `delta` is coloured by direction; `hint` explains the comparison. */
export function Kpi({
  label,
  value,
  hint,
  delta,
  loading,
}: {
  label: string
  value: string
  hint?: string
  delta?: number | null
  loading?: boolean
}) {
  const tone = toneOf(delta)
  return (
    <Card>
      <CardBody>
        <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {label}
        </p>
        {loading ? (
          <Skeleton className="mt-2 h-7 w-28" />
        ) : (
          <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-900 dark:text-slate-100">
            {value}
          </p>
        )}
        <p className="mt-0.5 flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
          {delta !== undefined && (
            <span
              className={cn(
                'font-medium tabular-nums',
                tone === 'up' && 'text-success',
                tone === 'down' && 'text-danger',
              )}
            >
              {formatDeltaPct(delta)}
            </span>
          )}
          {hint}
        </p>
      </CardBody>
    </Card>
  )
}

// --- charts ----------------------------------------------------------------------------

/**
 * Revenue and margin by day.
 *
 * Both on one axis rather than two: they are the same currency, and a second axis makes a
 * margin line that happens to sit above the revenue line, which is not a thing that can
 * happen. Margin is drawn over revenue so the gap between them is the cost of sales.
 */
export function RevenueTrend({ series }: { series: SeriesPoint[] }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={series} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id="analytics-net" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={CHART_COLORS.net} stopOpacity={0.35} />
            <stop offset="100%" stopColor={CHART_COLORS.net} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
        <XAxis
          dataKey="day"
          tickFormatter={formatDay}
          tick={AXIS}
          tickLine={false}
          minTickGap={24}
        />
        <YAxis
          tickFormatter={formatCompact}
          tick={AXIS}
          tickLine={false}
          axisLine={false}
          width={48}
        />
        <Tooltip
          {...TOOLTIP_STYLE}
          formatter={(value: number, name: string) => [formatMoney(value), name]}
          labelFormatter={(day: string) => formatDay(day)}
        />
        <Legend iconType="plainline" wrapperStyle={{ fontSize: '0.75rem' }} />
        <Area
          type="monotone"
          dataKey="net"
          name="Revenue"
          stroke={CHART_COLORS.net}
          fill="url(#analytics-net)"
          strokeWidth={2}
        />
        <Area
          type="monotone"
          dataKey="margin"
          name="Margin"
          stroke={CHART_COLORS.margin}
          fill="none"
          strokeWidth={2}
        />
      </AreaChart>
    </ResponsiveContainer>
  )
}

/** Order book against till, as a share of revenue. Two slices, and both are named. */
export function ChannelSplit({ channels }: { channels: ChannelSlice[] }) {
  const data = channels.map((slice) => ({
    name: CHANNEL_LABELS[slice.channel] ?? slice.channel,
    value: slice.net,
  }))
  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie data={data} dataKey="value" nameKey="name" innerRadius="55%" outerRadius="80%">
          {data.map((slice, index) => (
            <Cell key={slice.name} fill={SERIES_COLORS[index % SERIES_COLORS.length]} />
          ))}
        </Pie>
        <Tooltip {...TOOLTIP_STYLE} formatter={(value: number) => formatMoney(value)} />
        <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
      </PieChart>
    </ResponsiveContainer>
  )
}

/** Revenue by category. Horizontal, because category names do not fit under a tick. */
export function CategoryMix({ categories }: { categories: CategorySlice[] }) {
  const data = categories.slice(0, 8).map((slice) => ({
    name: slice.category_name ?? 'Uncategorised',
    net: slice.net,
  }))
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, bottom: 0, left: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
        <XAxis type="number" tickFormatter={formatCompact} tick={AXIS} tickLine={false} />
        <YAxis type="category" dataKey="name" tick={AXIS} tickLine={false} width={110} />
        <Tooltip {...TOOLTIP_STYLE} formatter={(value: number) => formatMoney(value)} />
        <Bar dataKey="net" name="Revenue" fill={CHART_COLORS.net} radius={[0, 4, 4, 0]} />
      </BarChart>
    </ResponsiveContainer>
  )
}

/** Top sellers by revenue. Same shape as the category chart, and for the same reason. */
export function ProductBars({ rows }: { rows: ProductRow[] }) {
  const data = rows.map((row) => ({
    name: row.product_name ?? row.product_code ?? row.product_id.slice(0, 8),
    net: row.net,
  }))
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, bottom: 0, left: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
        <XAxis type="number" tickFormatter={formatCompact} tick={AXIS} tickLine={false} />
        <YAxis type="category" dataKey="name" tick={AXIS} tickLine={false} width={140} />
        <Tooltip {...TOOLTIP_STYLE} formatter={(value: number) => formatMoney(value)} />
        <Bar dataKey="net" name="Revenue" fill={CHART_COLORS.net} radius={[0, 4, 4, 0]} />
      </BarChart>
    </ResponsiveContainer>
  )
}

/** How many customers sit in each RFM segment, and what they are worth. */
export function SegmentChart({ segments }: { segments: SegmentSlice[] }) {
  const data = segments.map((slice) => ({
    name: slice.segment.replace(/^\w/, (c) => c.toUpperCase()),
    value: slice.customers,
    monetary: slice.monetary,
  }))
  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie data={data} dataKey="value" nameKey="name" outerRadius="80%">
          {data.map((slice, index) => (
            <Cell key={slice.name} fill={SERIES_COLORS[index % SERIES_COLORS.length]} />
          ))}
        </Pie>
        <Tooltip
          {...TOOLTIP_STYLE}
          formatter={(value: number, _name, entry) => [
            `${value} customers · ${formatMoney((entry.payload as { monetary: number }).monetary)}`,
            (entry.payload as { name: string }).name,
          ]}
        />
        <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
      </PieChart>
    </ResponsiveContainer>
  )
}

/**
 * Attainment against target, with a line at how far through the month it is.
 *
 * The line is the point of the chart. A bar at 60% means nothing on its own; a bar at 60%
 * standing next to a month that is 70% gone means somebody is behind, and that is the
 * sentence the meeting starts with.
 */
export function AttainmentBars({
  reps,
  elapsedPct,
}: {
  reps: RepAttainment[]
  elapsedPct: number
}) {
  const data = reps.map((rep) => ({
    name: rep.rep_name ?? rep.employee_code ?? '—',
    attainment: rep.attainment_pct ?? 0,
    achieved: rep.achieved,
    target: rep.target,
  }))
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, bottom: 0, left: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
        <XAxis
          type="number"
          tickFormatter={(value: number) => `${value}%`}
          tick={AXIS}
          tickLine={false}
        />
        <YAxis type="category" dataKey="name" tick={AXIS} tickLine={false} width={130} />
        <Tooltip
          {...TOOLTIP_STYLE}
          formatter={(value: number, _name, entry) => {
            const row = entry.payload as { achieved: number; target: number }
            return [
              `${formatPct(value)} · ${formatMoney(row.achieved)} of ${formatMoney(row.target)}`,
              'Attainment',
            ]
          }}
        />
        <ReferenceLine
          x={elapsedPct}
          stroke={CHART_COLORS.muted}
          strokeDasharray="4 4"
          label={{ value: 'On pace', position: 'top', fontSize: 10, fill: '#94a3b8' }}
        />
        <Bar dataKey="attainment" name="Attainment" radius={[0, 4, 4, 0]}>
          {data.map((row) => (
            <Cell
              key={row.name}
              fill={row.attainment >= elapsedPct ? CHART_COLORS.margin : CHART_COLORS.cost}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  )
}

// --- small tables ----------------------------------------------------------------------

/**
 * What runs out next, or what is sitting still. One component for both because they are
 * the same row read from opposite ends of the same sort.
 */
export function CoverList({ rows, tone }: { rows: CoverRow[]; tone: 'risk' | 'idle' }) {
  return (
    <ul className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
      {rows.map((row) => (
        <li key={row.product_id} className="flex items-center justify-between gap-3 py-2">
          <span className="min-w-0">
            <span className="block truncate text-slate-700 dark:text-slate-300">
              {row.product_name ?? row.product_code ?? row.product_id.slice(0, 8)}
            </span>
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {row.on_hand} on hand · {row.daily_demand.toFixed(2)}/day
            </span>
          </span>
          <span
            className={cn(
              'shrink-0 tabular-nums text-sm font-medium',
              tone === 'risk' ? 'text-danger' : 'text-slate-500 dark:text-slate-400',
            )}
          >
            {row.days_of_cover === null ? 'No demand' : `${row.days_of_cover.toFixed(0)} days`}
          </span>
        </li>
      ))}
    </ul>
  )
}

/** A card that is just a header and a body, for the small tables above. */
export function PanelCard({
  title,
  description,
  isLoading,
  error,
  isEmpty,
  emptyMessage,
  children,
}: {
  title: string
  description?: string
  isLoading: boolean
  error: unknown
  isEmpty: boolean
  emptyMessage: string
  children: ReactNode
}) {
  return (
    <Card>
      <CardHeader title={title} description={description} />
      <CardBody>
        <QueryState
          isLoading={isLoading}
          error={error}
          isEmpty={isEmpty}
          emptyMessage={emptyMessage}
          skeleton={
            <div className="space-y-3">
              {[0, 1, 2, 3].map((row) => (
                <Skeleton key={row} className="h-8 w-full" />
              ))}
            </div>
          }
        >
          {children}
        </QueryState>
      </CardBody>
    </Card>
  )
}

/**
 * The window every analytics screen is read through, and the date it ends on.
 *
 * `asOf` is shown rather than implied. The window is measured back from the last day the
 * business traded, which on a demo or a staging copy is not today -- and "last 30 days"
 * next to figures that stop three weeks ago is the kind of thing that gets a dashboard
 * distrusted for good.
 */
export function WindowPicker({
  days,
  onChange,
  asOf,
}: {
  days: number
  onChange: (days: number) => void
  asOf?: string
}) {
  return (
    <div className="flex items-center gap-3">
      {asOf && (
        <span className="hidden text-xs text-slate-500 tablet:inline dark:text-slate-400">
          to {formatFullDay(asOf)}
        </span>
      )}
      <div
        className="flex rounded-md border border-slate-300 dark:border-slate-700"
        role="group"
        aria-label="Reporting window"
      >
        {WINDOWS.map((window) => (
          <button
            key={window.days}
            type="button"
            onClick={() => onChange(window.days)}
            aria-pressed={days === window.days}
            className={cn(
              'px-2.5 py-1 text-xs font-medium first:rounded-l-md last:rounded-r-md',
              days === window.days
                ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200'
                : 'text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800',
            )}
          >
            {window.label}
          </button>
        ))}
      </div>
    </div>
  )
}
